import React, { FC, forwardRef } from 'react';

import { IconProps } from '../types';
import clsx from 'clsx';

import styles from './style.module.css';
import { ButtonType, Intent, Size } from './types';
import { Loader } from '../Loader';

export type ButtonProps = {
  variant?: ButtonType;
  intent?: Intent;
  size?: Size;
  children?: React.ReactNode;
  className?: string;
  IconBefore?: FC<IconProps>;
  IconAfter?: FC<IconProps>;
  loading?: boolean;
  fullWidth?: boolean;
} & React.ButtonHTMLAttributes<HTMLButtonElement>;

export type Ref = HTMLButtonElement;

export const Button = forwardRef<Ref, ButtonProps>(
  ({ fullWidth = false, IconBefore, IconAfter, loading = false, ...props }, ref) => {
    const {
      children,
      size = 'l',
      intent = 'none',
      className,
      onClick,
      variant = 'default',
      disabled = false
    } = props;
    const handle = !loading && !disabled ? onClick : undefined;

    const iconColor =
      intent === 'brand' ? 'var(--color-action-primary-normal)' : 'var(--color-content-primary)';

    return (
      <button
        ref={ref}
        onClick={handle}
        {...props}
        className={clsx(
          styles.base,
          styles[variant],
          styles[`_${intent}`],
          styles[`_${size}`],
          loading && styles[`_loading`],
          disabled && styles[`_disabled`],
          fullWidth && styles[`_full`],
          className
        )}
      >
        {loading && (
          <div className={styles.loader}>
            <Loader />
          </div>
        )}
        <span>
          {IconBefore && <IconBefore size={size as any} color={iconColor} />}
          {children}
          {IconAfter && <IconAfter size={size as any} color={iconColor} />}
        </span>
      </button>
    );
  }
);

Button.displayName = 'Button';

export default Button;
